import { ListRowProps } from "@logicsoftware/react-virtualized";
import classNames from "classnames";
import * as React from "react";
import { Component, useCallback, useEffect, useRef } from "react";
import { UnionAppEntity } from "~/normalizr/entities";
import { commonClientResx } from "~/resxDist/commonClientResx";
import { Box } from "~/sharedComponents/layout";
import { CssProps } from "~/sharedComponents/layout/cssProps";
import { Highlighter } from "../highlighter";
import { Icon, icon, IconNames } from "../icons";
import { Center } from "../layout/Flex";
import { Text, TextProps } from "../layout/Text";
import { getRowStyle } from "../listRows";
import { Qtip, showOnHoverAndClick } from "../tooltip";
import { ListBaseProps } from "./List";
import { ListDeleteDefaultRow } from "./ListDeleteDefaultRow";
import { ListRowDefault } from "./ListRowDefault";
import { isSelectable } from "./utils/listUtils";
import * as listStyles from "./VirtList.less";

export interface InternalListProps<ItemType> extends ListBaseProps<ItemType> {
    // required only for PlainList, but due to the BaseList make it optional
    focusedItem?: ItemType;
    onSelect?: (item: ItemType) => void;
    setFocusedItem?: (item: ItemType) => void;
}

export interface MandatoryCommonListProps {
    height: number;
}

export class BaseList<ItemType, P extends InternalListProps<ItemType>, TState> extends Component<P, TState> {
    rowExtraProps = {};

    defaultDeleteListRowComponent: React.FC<VirtListRowProps<ItemType>> = ({ item, ...props }) => (
        <ListDeleteDefaultRow {...props} onDelete={() => this.props.onDelete(item)} item={item} />
    );

    protected get listRowComponent(): React.FC<VirtListRowProps<ItemType>> {
        return (
            this.props.listRowComponent ?? (this.props.onDelete ? this.defaultDeleteListRowComponent : ListRowDefault)
        );
    }

    getItemSelected = (item: ItemType) => (this.props.getItemSelected ? this.props.getItemSelected(item) : false);
    getIsMarkedList = () => !!this.props.getItemSelected;

    rowRenderer = ({ index, key, style }: Partial<ListRowProps>) => {
        const renderingItem = this.props.items[index];
        const isFocused = this.props.focusedItem === renderingItem;
        const renderAddRow = renderingItem?.addRow;
        const addRowComponent = renderingItem?.addRowComponent ?? ListDefaultAddRow;
        const ListRow = renderAddRow ? addRowComponent : this.listRowComponent;

        return (
            <ListRowWrapper<ItemType>
                key={key}
                style={style}
                item={renderingItem}
                onSelect={this.props.onSelect}
                setFocusedItem={this.props.setFocusedItem}
                isItemDisabled={this.props.isItemDisabled}
                isFocused={isFocused}
                {...this.rowExtraProps}
            >
                <ListRow
                    item={renderingItem}
                    isFocused={isFocused}
                    textForHighlight={this.props.searchText}
                    index={index}
                    items={this.props.items}
                    getItemLabel={this.props.getItemLabel}
                    getItemType={this.props.getItemType}
                    getItemClassName={this.props.getItemClassName}
                    isItemDisabled={this.props.isItemDisabled}
                    disabledItemTooltip={this.props.disabledItemTooltip}
                    isCreating={this.props.isCreating}
                    selected={this.getItemSelected(renderingItem)}
                    isMarkedList={this.getIsMarkedList()}
                />
            </ListRowWrapper>
        );
    };
}

export interface VirtListRowProps<ItemType> {
    item: ItemType;
    isFocused?: boolean;
    textForHighlight?: string;
    index?: number;
    items?: ItemType[];
    getItemLabel?: (item: ItemType) => string;
    getItemType?: (item: ItemType) => UnionAppEntity;
    getItemClassName?: (item: ItemType, items: ItemType[]) => string;
    isItemDisabled?: (item: ItemType) => boolean;
    disabledItemTooltip?: string;
    isCreating?: boolean;
    isMarkedList?: boolean;
    selected?: boolean;
}

export interface VirtListRowWrapperProps<ItemType> extends VirtListRowProps<ItemType> {
    onSelect?: (item: ItemType) => void;
    setFocusedItem?: (item: ItemType) => void;
    style?: React.CSSProperties;
    scrollIntoView?: (el: HTMLDivElement) => void;
}

export type ItemAdditionalOptionsBase = {
    group?: boolean;
};

export type ItemAdditionalOptions<ItemType = any> = ItemAdditionalOptionsBase & {
    addRow?: boolean;
    addAction?: (item: ItemType, callback?: any) => void;
    addRowComponent?: React.SFC<VirtListRowProps<ItemType>>;
    newValue?: string;
};

function ListRowWrapper<ItemType extends ItemAdditionalOptions<ItemType>>(
    props: React.PropsWithChildren<VirtListRowWrapperProps<ItemType>>
) {
    const listItemRef = useRef();
    const { isFocused, scrollIntoView, item, onSelect, setFocusedItem, isItemDisabled, style, children } = props;

    useEffect(() => {
        if (isFocused) {
            scrollIntoView?.(listItemRef.current);
        }
    }, [isFocused, scrollIntoView]);

    const handleClick = useCallback(() => {
        if (!isItemDisabled?.(item)) {
            onSelect?.(item);
        }
    }, [item, onSelect]);

    const onHover = useCallback(() => {
        if (!isItemDisabled?.(item)) {
            setFocusedItem?.(item);
        }
    }, [setFocusedItem, item]);

    const readonly = !isSelectable(item) || isItemDisabled?.(item);

    return (
        <div
            style={style}
            onClick={handleClick}
            onMouseMove={onHover}
            onMouseOver={onHover}
            ref={listItemRef}
            role="option"
            aria-readonly={readonly}
            aria-current={isFocused}
        >
            {children}
        </div>
    );
}

export type ListBaseAdditionalRowProps = {
    iconName?: IconNames;
    label?: string;
    typeLabel?: string;
    textForHighlight?: string;
    fsBlock?: boolean;
    disabledAddRowTooltip?: string;
} & Pick<TextProps, "weight">;

export const ListBaseAdditionalRow = <TItem extends {}>(
    props: VirtListRowProps<TItem> & ListBaseAdditionalRowProps & CssProps & { "data-testid"?: string }
) => {
    const {
        iconName = icon.plus,
        label = commonClientResx.add,
        isCreating,
        textForHighlight,
        fsBlock,
        typeLabel,
        weight = "normal",
        isFocused,
        getItemLabel,
        getItemType,
        getItemClassName,
        isItemDisabled,
        "data-testid": dataTestId,
        disabledItemTooltip,
        disabledAddRowTooltip,
        isMarkedList,
        ...cssProps
    } = props;

    const isDisabled = isItemDisabled?.(props.item);
    const disabledTooltip = isDisabled && disabledAddRowTooltip;
    const hightLightedText = textForHighlight && <b className={fsBlock ? "fs-block" : ""}>{textForHighlight}</b>;

    return (
        <Qtip isVisible={!!disabledTooltip} {...showOnHoverAndClick}>
            <Box {...cssProps} className={getRowStyle(props)} data-testid={dataTestId ?? "row"}>
                <div className={listStyles.row_with_icon}>
                    <Center width={24} flex="1 0">
                        <Icon iconName={isCreating ? icon.spinner : iconName} />
                    </Center>
                    <Text truncate={true} weight={weight}>
                        {label} {hightLightedText} {typeLabel}
                    </Text>
                </div>
            </Box>
            <>{disabledTooltip}</>
        </Qtip>
    );
};

const ListDefaultAddRow = <TItem extends {}>(props: VirtListRowProps<TItem> & ListBaseAdditionalRowProps) => (
    <ListBaseAdditionalRow {...props} />
);

export const ListDefaultGroupRow = <TItem extends {}>(props: VirtListRowProps<TItem>) => {
    const { textForHighlight, selected, getItemLabel, item } = props;

    return (
        <div className={classNames(getRowStyle(props), listStyles.group_row, listStyles.bold)}>
            <Highlighter
                showTitle={true}
                className={listStyles.label}
                text={getItemLabel(item)}
                textForHighlight={textForHighlight}
            />
            <Icon className={classNames(selected && listStyles.arrow_selected)} iconName={icon.chevron_right} />
        </div>
    );
};
